import { useMemo } from "react"

import { cn } from "@productlane/lib/src/cn"
import { highlightCodeBlocksHtml } from "@productlane/lib/src/highlight-code"
import {
	AttachmentsList,
	formatSlackAttachments,
	typographyVariants,
} from "@productlane/ui"

import type { TimelineEventProps } from "./types"
import { sanitizeMessageHtml } from "@/lib/sanitize-message-html"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import { DeviceInfoLine } from "./device-info-line"
import { AI_CHAT_ACTOR_NAME } from "./improve-reply"
import { ImproveReplyButton } from "./improve-reply-panel"

export function ChatMessageEvent({
	event,
	showAvatar = true,
	deviceInfoText,
}: TimelineEventProps) {
	const { thread } = useThreadContext()

	const companyLogoUrl = thread.company?.logoUrl || undefined
	const companyName = thread.company?.name || undefined

	const attachments = useMemo(() => {
		if (event.type !== "chatMessage") return []
		const attachmentsValue = event.chatMessage.attachments
		if (!attachmentsValue || !Array.isArray(attachmentsValue)) return []
		return attachmentsValue as unknown[]
	}, [event])

	const formattedAttachments = useMemo(
		() => formatSlackAttachments(attachments),
		[attachments],
	)

	if (event.type !== "chatMessage") {
		return null
	}

	const isFromAgent = event.isFromAgent || event.isAgentReply
	const platform = event.chatMessage.platform
	const source = platform === "teams" ? "teams" : null

	return (
		<SlackStyleMessage
			showAvatar={showAvatar}
			senderName={
				isFromAgent ? AI_CHAT_ACTOR_NAME : event.chatMessage.fromContactName
			}
			senderImage={event.chatMessage.fromContactImage}
			avatarContent={isFromAgent ? <AgentMessageAvatar /> : undefined}
			timestamp={new Date(event.chatMessage.createdAt)}
			companyName={companyName}
			companyLogoUrl={companyLogoUrl}
			source={source}
			isOutbound={event.chatMessage.type === "OUTBOUND"}
			footer={
				deviceInfoText ? <DeviceInfoLine text={deviceInfoText} /> : undefined
			}
		>
			{event.chatMessage.html ? (
				<div
					dangerouslySetInnerHTML={{
						__html: highlightCodeBlocksHtml(
							sanitizeMessageHtml(event.chatMessage.html),
						),
					}}
					className={cn(
						"html-content",
						typographyVariants({
							variant: "message",
							className: "min-w-full overflow-auto",
						}),
					)}
				/>
			) : (
				// The agent's Teams replies are stored as plain text alone.
				<div
					className={typographyVariants({
						variant: "message",
						className: "min-w-full whitespace-pre-wrap",
					})}
				>
					{event.chatMessage.text}
				</div>
			)}
			{formattedAttachments.length > 0 && (
				<>
					{event.chatMessage.html && <hr className="my-3" />}
					<AttachmentsList
						attachments={formattedAttachments}
						enableViewer
						showPointer={false}
					/>
				</>
			)}
			{event.isAgentReply && (
				<ImproveReplyButton
					threadId={thread.id}
					channel="teams"
					messageId={event.chatMessage.id}
				/>
			)}
		</SlackStyleMessage>
	)
}
